import { useCallback, useEffect, useRef, useState } from "react"

import { cn } from "@productlane/lib/src/cn"
import { macroVariableToken } from "@productlane/lib/src/macro-variables"

import type { MacroVariableListProps } from "./types"

export default function MacroVariableList({
	items,
	command,
	setOnKeyDownHandler,
}: MacroVariableListProps) {
	const [selected, setSelected] = useState(0)
	const selectedRef = useRef(0)

	const moveSelection = useCallback((index: number) => {
		selectedRef.current = index
		setSelected(index)
	}, [])

	useEffect(() => moveSelection(0), [items, moveSelection])

	const select = useCallback(
		(index: number) => {
			const item = items[index]
			if (!item) return
			command({ key: item.key })
		},
		[command, items],
	)

	useEffect(() => {
		setOnKeyDownHandler(({ event }) => {
			if (items.length === 0) return false

			switch (event.key) {
				case "ArrowUp":
					moveSelection((selectedRef.current + items.length - 1) % items.length)
					return true
				case "ArrowDown":
					moveSelection((selectedRef.current + 1) % items.length)
					return true
				case "Enter":
				case "Tab":
					select(selectedRef.current)
					return true
				default:
					return false
			}
		})
	}, [items, moveSelection, select, setOnKeyDownHandler])

	if (items.length === 0) return null

	return (
		<div className="max-h-64 min-w-72 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 pb-2 shadow-md dark:border-gray-700/80 dark:bg-gray-900">
			<ul className="m-0 flex list-none flex-col p-0">
				{items.map((item, index) => (
					<li key={item.key}>
						{index > 0 && items[index - 1]?.group !== item.group && (
							<div className="my-1 h-px bg-gray-200 dark:bg-gray-700/80" />
						)}
						<button
							type="button"
							onClick={() => select(index)}
							onMouseMove={() => moveSelection(index)}
							className={cn(
								"flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-left",
								selected === index && "bg-gray-100 dark:bg-gray-700/50",
							)}
						>
							<span className="flex-none font-mono text-xs text-secondary">
								{macroVariableToken(item.key)}
							</span>
							<span className="truncate text-sm">{item.label}</span>
							<span className="ml-auto flex-none text-xs text-tertiary">
								{item.group}
							</span>
						</button>
					</li>
				))}
			</ul>
		</div>
	)
}
